import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Filler, Title, Tooltip, Legend } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Filler, Title, Tooltip, Legend, zoomPlugin);

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

// Status palette: green (operational) at the top, red (outage) at the bottom,
// so the line reads as "red at the bottom" regardless of where an outage falls
// on the x-axis. The fill underneath fades from the same green to white.
const GOOD = '12, 163, 12';
const CRITICAL = '208, 59, 59';

const verticalGradient = (context, topStop, bottomStop) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;
  if (!chartArea) return null; // not yet laid out on first render
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, topStop);
  gradient.addColorStop(1, bottomStop);
  return gradient;
};

const UptimeChart = () => {
  const [outages, setOutages] = useState([]);
  const [uptimePercentage, setUptimePercentage] = useState(null);

  // Fetch outage data from the server
  useEffect(() => {
    const fetchOutages = async () => {
      const response = await fetch('/outages');
      const data = await response.json();
      setOutages(data);
    };

    fetchOutages();
  }, []);

  // Fetch the overall uptime percentage
  useEffect(() => {
    const fetchUptime = async () => {
      const response = await fetch('/uptime');
      const data = await response.json();
      setUptimePercentage(data.uptime_percentage);
    };

    fetchUptime();
  }, []);

  // Prepare data for the chart
  const data = {
    labels: [], // For X-axis (time, e.g., dates or timestamps)
    datasets: [
      {
        label: 'Operational Status',
        data: [], // For Y-axis (1 = operational, 0 = outage)
        borderColor: (context) => verticalGradient(context, `rgb(${GOOD})`, `rgb(${CRITICAL})`) || `rgb(${GOOD})`,
        backgroundColor: (context) => verticalGradient(context, `rgba(${GOOD}, 0.35)`, 'rgba(255, 255, 255, 0)') || `rgba(${GOOD}, 0.35)`,
        borderWidth: 2,
        fill: true,
        stepped: true, // hold the previous value until the next edge point instead of interpolating
      },
    ],
  };

  // Fill in only the edge points: the moment an outage starts (drop to 0)
  // and the moment it ends (recovery to 1). No intermediate samples needed.
  outages.forEach((outage) => {
    data.labels.push(new Date(outage.start));
    data.datasets[0].data.push(0); // Outage starts

    if (outage.end) {
      data.labels.push(new Date(outage.end));
      data.datasets[0].data.push(1); // Outage ends
    }
    // else: outage is still ongoing (no end yet) — leave it open, the trailing
    // "now" point below carries it forward at 0 instead of a fake recovery.
  });

  // Extend the line to the current moment at whatever the last known status was,
  // so an ongoing outage (or ongoing uptime) doesn't visually stop in the past.
  const now = new Date();
  const lastStatus = data.datasets[0].data.length
    ? data.datasets[0].data[data.datasets[0].data.length - 1]
    : 1; // no outages recorded at all -> assume operational
  data.labels.push(now);
  data.datasets[0].data.push(lastStatus);

  // Earliest label bounds how far back panning/zooming out is allowed to go.
  const earliestLabel = data.labels.length ? data.labels[0].getTime() : now.getTime() - TWO_DAYS_MS;

  // Default view is the last two days, but if the whole dataset is younger
  // than that, don't pad it out with empty space — start from its first point.
  const defaultMin = Math.max(earliestLabel, now.getTime() - TWO_DAYS_MS);

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false, // fill the sized wrapper below instead of computing its own height
    scales: {
      x: {
        type: 'time',
        min: defaultMin,
        max: now.getTime(),
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          source: 'data', // only show ticks at actual edge points, not every minute
          autoSkip: true,
        },
      },
      y: {
        min: 0, // Lower the Y-axis to 0 for outages
        max: 1, // Operational level is 1, outage level is 0
        title: {
          display: true,
          text: 'Operational Status',
        },
      },
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
        limits: {
          x: { min: earliestLabel, max: now.getTime() },
        },
      },
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <h2>Uptime and Outages</h2>
      <div style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
        <Line data={data} options={options} />
      </div>
      {uptimePercentage !== null && (
        <div style={{ textAlign: 'center' }}>Uptime: {uptimePercentage}%</div>
      )}
    </div>
  );
};

export default UptimeChart;
